<template>
  <div>
    <!-- Login Required Alert (for non-logged-in users) -->
    <v-alert
      v-if="!isLoggedIn"
      color="primary"
      prominent
      border="left"
      class="mb-4 login-required-alert"
      dark
    >
      <v-row align="center" no-gutters>
        <v-col cols="auto" class="mr-4">
          <v-icon size="48" color="white">mdi-account-lock</v-icon>
        </v-col>
        <v-col>
          <div class="text-h6 font-weight-bold mb-2">Login Required</div>
          <div class="text-body-1">
            To export your bookmarks from LinkBook.io, you need to be logged in.
            Create a free account to get started!
          </div>
        </v-col>
      </v-row>
      <v-row class="mt-4">
        <v-col cols="12" sm="auto" class="pb-2 pb-sm-3">
          <v-btn
            color="white"
            href="/login"
            large
            block
            elevation="2"
            class="primary--text font-weight-bold"
          >
            <v-icon left>mdi-login</v-icon>
            Login
          </v-btn>
        </v-col>
        <v-col cols="12" sm="auto" class="pb-2 pb-sm-3">
          <v-btn
            color="success"
            href="/signup"
            large
            block
            elevation="2"
            class="font-weight-bold"
          >
            <v-icon left>mdi-account-plus</v-icon>
            Sign Up Free
          </v-btn>
        </v-col>
      </v-row>
    </v-alert>

    <!-- What is Export -->
    <v-card class="mb-4" elevation="2">
      <v-card-title class="primary white--text">
        <v-icon left color="white">mdi-information</v-icon>
        What is Bookmark Export?
      </v-card-title>
      <v-card-text class="pt-4">
        <p class="mb-3">
          Export creates an HTML file containing all your LinkBook.io bookmarks
          in a standard format that can be imported into:
        </p>
        <v-list dense class="transparent">
          <v-list-item>
            <v-list-item-icon>
              <v-icon color="primary">mdi-check-circle</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title
                >Any web browser (Chrome, Firefox, Safari, Edge,
                etc.)</v-list-item-title
              >
            </v-list-item-content>
          </v-list-item>
          <v-list-item>
            <v-list-item-icon>
              <v-icon color="primary">mdi-check-circle</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title
                >Other bookmark management services</v-list-item-title
              >
            </v-list-item-content>
          </v-list-item>
          <v-list-item>
            <v-list-item-icon>
              <v-icon color="primary">mdi-check-circle</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title
                >As a backup of your bookmarks</v-list-item-title
              >
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>

    <!-- Export Statistics -->
    <v-card v-if="isLoggedIn && hasBookmarks" class="mb-4" elevation="2">
      <v-card-title>
        <v-icon left>mdi-chart-box</v-icon>
        Your Bookmark Statistics
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6">
            <v-card color="primary" dark>
              <v-card-text class="text-center">
                <div class="text-h3 font-weight-bold">{{ totalBookmarks }}</div>
                <div class="text-subtitle-1">Total Bookmarks</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6">
            <v-card color="secondary" dark>
              <v-card-text class="text-center">
                <div class="text-h3 font-weight-bold">
                  {{ totalCategories }}
                </div>
                <div class="text-subtitle-1">Categories</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Export Action -->
    <v-card
      class="mb-4"
      elevation="2"
      :class="{ 'disabled-overlay': !isLoggedIn }"
    >
      <v-card-title class="text-h6">
        <v-icon left>mdi-download-circle</v-icon>
        Export Your Bookmarks
      </v-card-title>
      <v-card-subtitle class="pb-0">
        Download all your bookmarks as an HTML file
      </v-card-subtitle>
      <v-card-text>
        <v-alert
          v-if="isLoggedIn && !hasBookmarks"
          type="warning"
          text
          outlined
          class="mb-4"
        >
          <strong>No bookmarks found!</strong>
          <div class="text-body-2 mt-1">
            You need to add some bookmarks before you can export them. Use the
            <v-icon small>mdi-plus</v-icon> button to add bookmarks.
          </div>
        </v-alert>

        <v-alert v-if="exportSuccess" type="success" dismissible class="mb-4">
          <div>
            <strong>Export Successful!</strong>
            <div class="text-body-2 mt-1">
              Your bookmarks have been downloaded as "linkbook-export.html"
            </div>
          </div>
        </v-alert>

        <v-alert
          v-if="exportError"
          type="error"
          dismissible
          @input="exportError = null"
          class="mb-4"
        >
          {{ exportError }}
        </v-alert>

        <v-row align="center">
          <v-col cols="12" sm="auto">
            <v-btn
              color="primary"
              large
              :loading="isExporting"
              :disabled="!hasBookmarks || !isLoggedIn"
              @click="exportBookmarks"
              block
              class="text-none"
            >
              <v-icon left>mdi-download</v-icon>
              Export Bookmarks to HTML
            </v-btn>
          </v-col>

          <v-col v-if="isLoggedIn && hasBookmarks" cols="12" sm="auto">
            <v-chip color="primary" outlined>
              <v-icon left small>mdi-file-document</v-icon>
              {{ totalBookmarks }} bookmark{{ totalBookmarks !== 1 ? 's' : '' }}
              ready
            </v-chip>
          </v-col>
        </v-row>

        <div class="mt-4 text-caption grey--text">
          <v-icon small>mdi-clock-outline</v-icon>
          File format: Netscape Bookmark HTML (compatible with all browsers)
        </div>
      </v-card-text>
    </v-card>

    <!-- Import Link -->
    <v-alert type="info" text outlined border="left">
      <div>
        <strong>Need to import bookmarks?</strong>
        <div class="text-body-2 mt-1">
          You can import bookmarks from your browser or other services.
          <v-btn
            x-small
            color="info"
            text
            href="/import-bookmarks"
            class="ml-2"
          >
            Go to Import <v-icon right x-small>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </div>
    </v-alert>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'ExportBookmark',
  data() {
    return {
      isExporting: false,
      exportSuccess: false,
      exportError: null
    }
  },
  created() {
    // Fetch links to ensure we have the latest data (only if logged in)
    if (this.isLoggedIn) {
      this.fetchLinks()
    }
  },
  computed: {
    ...mapGetters(['getLinks', 'isLoggedIn']),
    hasBookmarks() {
      const { domains } = this.$store.state.links || {}
      if (!domains) return false

      // Check if there are any bookmarks in any domain
      return Object.values(domains).some(links => links && links.length > 0)
    },
    totalBookmarks() {
      const { domains } = this.$store.state.links || {}
      if (!domains) return 0

      // Count total bookmarks across all domains
      return Object.values(domains).reduce((total, links) => {
        return total + (links ? links.length : 0)
      }, 0)
    },
    totalCategories() {
      const { domains } = this.$store.state.links || {}
      if (!domains) return 0

      // Count non-empty domains/categories
      return Object.entries(domains).filter(
        ([_, links]) => links && links.length > 0
      ).length
    }
  },
  methods: {
    ...mapActions(['fetchLinks']),
    async exportBookmarks() {
      this.isExporting = true
      this.exportSuccess = false
      this.exportError = null

      try {
        const { domains } = this.$store.state.links || {}
        if (!domains || Object.keys(domains).length === 0) {
          throw new Error('No bookmarks found to export')
        }

        // Generate HTML content in browser bookmark format
        const htmlContent = this.generateBookmarkHTML(domains)

        // Create and download the file
        this.downloadFile(htmlContent, 'linkbook-export.html')

        this.exportSuccess = true
      } catch (error) {
        console.error('Export error:', error)
        this.exportError = error.message || 'Failed to export bookmarks'
      } finally {
        this.isExporting = false
      }
    },

    generateBookmarkHTML(domains) {
      const now = new Date()

      let html = `<!DOCTYPE NETSCAPE-Bookmark-file-1>
<!-- This is an automatically generated file.
     It will be read and overwritten.
     DO NOT EDIT! -->
<META HTTP-EQUIV="Content-Type" CONTENT="text/html; charset=UTF-8">
<TITLE>LinkBook.io Bookmarks</TITLE>
<H1>LinkBook.io Bookmarks</H1>
<DL><p>`

      // Process each domain/category
      Object.entries(domains).forEach(([domain, links]) => {
        if (links && links.length > 0) {
          // Create a folder for each domain
          html += `\n    <DT><H3 ADD_DATE="${Math.floor(
            now.getTime() / 1000
          )}" LAST_MODIFIED="${Math.floor(
            now.getTime() / 1000
          )}">${this.escapeHtml(domain)}</H3>`
          html += `\n    <DL><p>`

          // Add each link in the domain
          links.forEach(link => {
            const addDate = link.date_updated
              ? Math.floor(link.date_updated.toDate().getTime() / 1000)
              : Math.floor(now.getTime() / 1000)
            const title = this.escapeHtml(link.title || link.link)
            const url = this.escapeHtml(link.link)

            html += `\n        <DT><A HREF="${url}" ADD_DATE="${addDate}" LAST_MODIFIED="${addDate}">${title}</A>`
          })

          html += `\n    </DL><p>`
        }
      })

      html += `\n</DL><p>`

      return html
    },

    escapeHtml(text) {
      const div = document.createElement('div')
      div.textContent = text
      return div.innerHTML
    },

    downloadFile(content, filename) {
      const blob = new Blob([content], { type: 'text/html;charset=utf-8' })
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up the URL object
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
/* Disabled overlay for non-logged-in users */
.disabled-overlay {
  position: relative;
}

.disabled-overlay::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  pointer-events: none;
  z-index: 1;
}

/* Login Required Alert Styling */
.login-required-alert {
  border-left-width: 8px !important;
}

.login-required-alert .v-icon {
  opacity: 1 !important;
}
</style>
